import type { Contact, ContactCreatePayload, ContactCreateResult, ContactUpdatePayload, MinorEligibility } from '@/types/person/contacts';

import { contactsService } from '~/services/contacts-service';
import { memberFacingMessage } from '~/utils/errors';

export const useContactsStore = defineStore('contacts', () => {
    const personStore = usePersonStore();
    const { showSuccess, showError } = useGlobalToast();
    const selectedContact = ref<Contact | null>(null);
    const pendingUpdates = new Set<string>();

    //state
    const contacts = ref<Contact[]>([]);
    const fetchFailed = ref(false);
    /**
     * A read of the list has completed, whatever it returned. An empty list and a list that
     * never arrived both leave `contacts` empty, so callers that read once per session guard on
     * this rather than on the length: an account with no family members would otherwise ask the
     * endpoint again on every open.
     */
    const loaded = ref(false);
    /**
     * How many reads of the list have landed. What a page shows on the rows from another read
     * follows this, so it is read again each time the rows are: adding a member or giving one a
     * license changes more than the rows themselves.
     */
    const listReads = ref(0);
    const selfMember = ref<Contact | null>(null);
    const searchQuery = ref('');
    const minorConsentVariant = ref<'standard' | 'bekiares'>('standard');

    const tags = ref([
        { name: 'Friends', code: 'friend' },
        { name: 'Gamers', code: 'gamer' },
        { name: 'Family', code: 'family' },
        { name: 'Coworkers', code: 'coworker' },
    ]);

    // Computed getter for filtered contacts
    const filteredContacts = computed(() => {
        let filtered = contacts.value;

        // Apply search filter
        if (searchQuery.value) {
            const lowerCaseTerm = searchQuery.value.toLowerCase();
            filtered = filtered.filter((contact) =>
                contact.name.toLowerCase().includes(lowerCaseTerm)
            );
        }

        return filtered;
    });

    /**
     * The family as both views list it: the member themself first, then everyone the current
     * search leaves. Self is not a contact row — it comes from the endpoint's `self` envelope —
     * so the two have to be stitched together, once, here.
     */
    const allFamilyMembers = computed((): Contact[] => {
        const members: Contact[] = [];

        if (selfMember.value) members.push(selfMember.value);
        members.push(...filteredContacts.value);

        return members;
    });

    /**
     * What a screen reader is told about the list. Each view swaps its states in whole and passes
     * its own loading flag — the table fetches on mount, the cards are handed one.
     */
    const familyStatus = computed(() => (isLoading: boolean): string => {
        if (isLoading) return 'Loading family members';
        // An empty store after a failed fetch is not an empty family; the error toast owns that.
        if (fetchFailed.value) return '';
        const count = allFamilyMembers.value.length;
        if (!count) return 'You have no family members yet';
        return `${count} family ${count === 1 ? 'member' : 'members'}`;
    });

    async function fetchContacts(includeSelf: boolean = true) {
        if (!personStore.person) return;

        fetchFailed.value = false;

        try {
            const response = await contactsService.getContacts(personStore.person.id, includeSelf);
            contacts.value = response.data;
            selfMember.value = response.self || null;
            listReads.value++;
        } catch (error: unknown) {
            // The failure is swallowed here so the page still renders, which leaves callers
            // unable to tell "no family members" from "the list never arrived". They read this.
            fetchFailed.value = true;
            showError('Error', memberFacingMessage(error, 'Failed to fetch contacts'));
        } finally {
            loaded.value = true;
        }
    }

    /**
     * Create a contact, and hand back the row it became where the list can still be read.
     *
     * CREATED and RESOLVED are two different facts and callers need both. The create endpoint is
     * a bulk sync that answers with nothing, so the refreshed list is the only place the new row
     * exists: it is matched on the email it was created with, which the form has already
     * established is on no other contact. A refresh that fails leaves the member created and
     * unresolvable — reporting that as a failed create would strand a form on a member the
     * account now holds, which is why the two are reported separately.
     */
    async function createContact(contactData: ContactCreatePayload): Promise<ContactCreateResult> {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return { created: false, contact: null };
        }

        try {
            await contactsService.createContact(personStore.person.id, contactData);
        } catch (error: unknown) {
            showError('Error', memberFacingMessage(error, 'Failed to create contact'));
            return { created: false, contact: null };
        }

        // The POST landed, so the member exists whatever the read below does.
        showSuccess("Success", 'Contact created successfully');

        await fetchContacts(); // the created row has no other home

        // Matched on the address it was created with, which the form established is on no other
        // member. A member created with no address is matched on its name instead, which the form
        // established the same way — never on one blank address meeting another.
        const email = contactData.emails[0]?.toLowerCase();
        const name = contactData.name.trim().toLowerCase();

        return {
            created: true,
            contact: contacts.value.find((contact) => email
                ? contact.emails?.some((entry) => entry.email?.toLowerCase() === email)
                : contact.name?.trim().toLowerCase() === name) ?? null,
        };
    }

    /**
     * Replace one contact. The endpoint returns the contact as it now stands, and that image
     * replaces the row here: a refetch of the whole list would be a second read of a record
     * this call already handed back, and it can come back older than the write.
     *
     * The payload replaces both detail lists. Concurrent replacements of one member would
     * overwrite each other's additions, so a caller retries after the held update finishes.
     */
    async function updateContact(contactId: number, payload: ContactUpdatePayload): Promise<Contact | null> {
        const personId = personStore.person?.id;
        if (!personId) {
            showError('Error', 'User not found');
            return null;
        }

        const key = `${personId}:${contactId}`;
        if (pendingUpdates.has(key)) {
            showError('Update in progress', 'This family member is being updated. Wait for it to finish, then try again.');
            return null;
        }
        pendingUpdates.add(key);

        try {
            const updated = await contactsService.updateContact(personId, contactId, payload);
            if (!updated) return null;

            // The returned member belongs to the account that started this write.
            if (personStore.person?.id === personId) {
                contacts.value = contacts.value.map((contact) => (contact.id === contactId ? updated : contact));
            }
            return updated;
        } catch (error: unknown) {
            showError('Error', memberFacingMessage(error, 'Failed to update contact'));
            return null;
        } finally {
            pendingUpdates.delete(key);
        }
    }

    const removeContact = async (contact_id: number) => {
        if (!personStore.person) return;

        try {
            await contactsService.deleteContact(personStore.person.id, contact_id);
            
            // Only remove the contact from the contacts after successful API call
            contacts.value = contacts.value.filter(contact => contact.id !== contact_id);
            await fetchContacts(); // Refresh the contacts list
            showSuccess("Success", 'Contact removed successfully');
            selectedContact.value = null;
        } catch (error: unknown) {
            showError('Error', memberFacingMessage(error, 'Something went wrong while removing contact'));
            // Don't update local state if the API call failed
        }
    };

    const searchContacts = (query: string) => {
        searchQuery.value = query;
    };

    const clearSearch = () => {
        searchQuery.value = '';
    };

    /**
     * Whether the member may add a child. A check that fails is thrown to the caller rather than
     * answered as "not eligible": the two read differently to the member, and only the caller
     * knows where to say which it was.
     */
    async function checkMinorEligibility(): Promise<MinorEligibility> {
        if (!personStore.person) {
            return { eligible: false, reason: 'User not found' };
        }

        const result = await contactsService.checkMinorEligibility(personStore.person.id);
        if (result?.agreement_variant) {
            minorConsentVariant.value = result.agreement_variant;
        }
        return result;
    }

    async function signMinorConsent(signerLegalName: string) {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return false;
        }

        try {
            await contactsService.signMinorConsent(personStore.person.id, signerLegalName);
            personStore.person.minor_consent_signed = true;
            personStore.person.minor_consent_signed_at = new Date().toISOString();
            return true;
        } catch (error: unknown) {
            showError('Error', memberFacingMessage(error, 'Failed to sign consent'));
            return false;
        }
    }

    async function createMinorChild(childData: {
        first_name: string;
        last_name: string;
        dob: string;
    }) {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return false;
        }

        try {
            await contactsService.createMinorChild(personStore.person.id, childData);
            await fetchContacts();
            showSuccess("Success", 'Child added to your family protection plan');
            return true;
        } catch (error: unknown) {
            showError('Error', memberFacingMessage(error, 'Failed to add child'));
            return false;
        }
    }

    return {
        contacts,
        fetchFailed,
        loaded,
        listReads,
        selfMember,
        selectedContact,
        fetchContacts,
        createContact,
        updateContact,
        tags,
        removeContact,
        searchContacts,
        clearSearch,
        searchQuery,
        filteredContacts,
        allFamilyMembers,
        familyStatus,
        checkMinorEligibility,
        signMinorConsent,
        createMinorChild,
        minorConsentVariant,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useContactsStore, import.meta.hot))
}